import { useTranslation } from 'react-i18next';
import { quantityText, unitPriceText } from '@/lib/lineDisplay';
import { formatCurrency } from '@/lib/format';
import type { Estimate } from '@/routes/estimates/useEstimates';

/**
 * A sent estimate, as it stands (M2-06): read-only, with the tax frozen at
 * send. Nothing here computes — every figure is the stored one, because a
 * document somebody has received keeps the number it was sent with.
 */
export function EstimateView({ estimate }: { estimate: Estimate }) {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="estimate-lines-heading" className="space-y-4">
      <h2 id="estimate-lines-heading" className="text-base font-semibold">
        {t('estimates.builder.heading')}
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[var(--color-muted-foreground)]">
            <tr>
              <th scope="col" className="py-2 pr-3 font-medium">
                {t('estimates.line.description')}
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                {t('estimates.line.quantity')}
              </th>
              <th scope="col" className="hidden py-2 pr-3 text-right font-medium sm:table-cell">
                {t('estimates.line.unitPrice')}
              </th>
              <th scope="col" className="hidden py-2 pr-3 font-medium sm:table-cell">
                {t('estimates.line.taxCategory')}
              </th>
              <th scope="col" className="py-2 text-right font-medium">
                {t('estimates.line.total')}
              </th>
            </tr>
          </thead>
          <tbody>
            {estimate.lines.map((line) => (
              <tr
                key={line.id}
                className="border-t border-[var(--color-border)]"
                data-testid="estimate-line"
              >
                <td className="py-2 pr-3">{line.description}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{quantityText(line, t)}</td>
                <td className="hidden py-2 pr-3 text-right tabular-nums sm:table-cell">
                  {unitPriceText(line, t)}
                </td>
                <td className="hidden py-2 pr-3 sm:table-cell">
                  {t(`estimates.line.taxCategories.${line.taxCategory}`, {
                    defaultValue: line.taxCategory,
                  })}
                </td>
                <td className="py-2 text-right tabular-nums">
                  {formatCurrency(Number(line.total))}
                  {Number(line.discount) > 0 ? (
                    <span className="block text-xs text-[var(--color-muted-foreground)]">
                      {t('estimates.line.discountTaken', {
                        amount: formatCurrency(Number(line.discount)),
                      })}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <dl
        className="ml-auto grid max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm"
        data-testid="estimate-totals"
      >
        <dt>{t('estimates.totals.subtotal')}</dt>
        <dd className="text-right tabular-nums" data-testid="subtotal">
          {formatCurrency(Number(estimate.subtotal))}
        </dd>
        <dt>{t('estimates.totals.tax')}</dt>
        <dd className="text-right tabular-nums" data-testid="tax">
          {formatCurrency(Number(estimate.tax))}
        </dd>
        <dt className="font-semibold">{t('estimates.totals.total')}</dt>
        <dd className="text-right font-semibold tabular-nums" data-testid="total">
          {formatCurrency(Number(estimate.total))}
        </dd>
      </dl>
    </section>
  );
}
